import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { SvgIconComponent } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PROFILE_MENU_ROUTES } from './header.constants';
import type { ProfileMenuItemId } from './header.rules';
import type { ProfileMenuItem } from './useProfileMenu';

type MenuEntryId = ProfileMenuItemId | 'recent';

interface MenuEntry {
  id: MenuEntryId;
  onSelect: () => void;
}

const ITEM_ICONS: Record<MenuEntryId, SvgIconComponent> = {
  profile: PersonIcon,
  notifications: NotificationsNoneIcon,
  recent: HistoryOutlinedIcon,
  logout: ExitToAppIcon,
  stopViewAs: VisibilityIcon,
  viewAs: VisibilityIcon,
  checkin: CheckCircleIcon,
  checkout: CheckIcon,
  attendance: CheckIcon,
  about: InfoIcon,
};

interface HeaderProfileMenuProps {
  id: string;
  anchorEl: HTMLElement | null;
  items: readonly ProfileMenuItem[];
  onClose: () => void;
}

const withRecentAlerts = (items: readonly ProfileMenuItem[], openRecent: () => void): readonly MenuEntry[] =>
  items.flatMap((item): MenuEntry[] => (item.id === 'notifications' ? [item, { id: 'recent', onSelect: openRecent }] : [item]));

export const HeaderProfileMenu = ({ id, anchorEl, items, onClose }: HeaderProfileMenuProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openRecent = () => {
    onClose();
    navigate(PROFILE_MENU_ROUTES.recent);
  };

  const labels: Record<MenuEntryId, string> = {
    profile: t('header.profile', 'Profile'),
    notifications: t('closeTheLoop.settings.title', 'Notifications'),
    recent: t('closeTheLoop.recent.title', 'Recent alerts'),
    logout: t('header.logout', 'Logout'),
    stopViewAs: t('header.stopViewAs', 'Stop View As'),
    viewAs: t('header.viewAs', 'View As'),
    checkin: t('header.checkin', 'Checkin'),
    checkout: t('header.checkout', 'Checkout'),
    attendance: t('header.attendance', 'Attendance'),
    about: t('header.about', 'About'),
  };

  return (
    <Menu
      MenuListProps={{ id }}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={anchorEl !== null}
      onClose={onClose}
    >
      {withRecentAlerts(items, openRecent).map(({ id: itemId, onSelect }) => {
        const Icon = ITEM_ICONS[itemId];
        return (
          <MenuItem key={itemId} onClick={onSelect}>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            <ListItemText primary={labels[itemId]} />
          </MenuItem>
        );
      })}
    </Menu>
  );
};
